import { AIShimmerOverlay } from "./ai/ai-info-extractor/ai-shimmer-overlay";
import CountrySelect from "./CountrySelect";
import FileUploader from "./FileUploader";
import { Select } from "./Select";
import { TextArea } from "./TextArea";
import { DatePickerInput } from "./ui/DatePickerInput";
import { Field, FieldError, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import type { FieldConfig } from "@/utils/formTypes";

interface DynamicFieldProps {
  value: any;
  error?: any;
  name: string;
  touched?: any;
  field: FieldConfig;
  onChange: (value: any) => void;
  disabled?: boolean;
  showAIShimmer?: boolean;
}

const DynamicField = ({
  name,
  value,
  error,
  field,
  touched,
  onChange,
  disabled,
  showAIShimmer = false,
}: DynamicFieldProps) => {
  const renderField = () => {
    switch (field.type) {
      case "select":
        return (
          <Select
            label={field.label}
            name={name}
            value={value}
            onChange={onChange}
            error={touched && error ? error : undefined}
            options={field.options || []}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        );
      case "text":
        return (
          <Field>
            <FieldLabel>{field.label}</FieldLabel>
            <AIShimmerOverlay show={showAIShimmer}>
              <Input
                value={value}
                type={field.type}
                disabled={disabled}
                placeholder={field.placeholder}
                onChange={(value) => onChange(value.target.value)}
              />
            </AIShimmerOverlay>
            <FieldError className="text-xs">{touched && error ? error : undefined}</FieldError>
          </Field>
        );
      case "textarea":
        return (
          <Field>
            <TextArea
              placeholder={field.placeholder ?? ""}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              name={name}
              error={touched && error ? error : undefined}
              label={field.label}
            />
          </Field>
        );
      case "file":
        return (
          <Field className={field.className}>
            <FieldLabel>{field.label}</FieldLabel>
            <FileUploader name={name} file={value} onFileChange={(file) => onChange(file)} accept={field.accept} />
            <FieldError className="text-xs">{touched && error ? error : undefined}</FieldError>
          </Field>
        );
      case "date":
        return (
          <>
            <DatePickerInput
              name={name}
              label={field.label}
              value={value ? new Date(value) : undefined}
              onChange={(value) => onChange(value)}
              error={touched && error ? error : undefined}
              disabled={disabled}
              showAIShimmer={showAIShimmer}
            />
          </>
        );
      case "country-select":
        return (
          <CountrySelect
            label={field.label}
            name={name}
            value={value}
            onChange={onChange}
            error={touched && error ? error : undefined}
            disabled={disabled}
            showAIShimmer={showAIShimmer}
          />
        );
      default:
        return null;
    }
  };

  return renderField();
};

export default DynamicField;
